/** *************************************************************
 * Copyright (c) 2006 - 2021, Case Anywhere, LLC.
 * All rights reserved
 *
 * @author Brad Wetmore
 * Date: 07/12/2018
 *
 * @version 0.8
 * Revisions:
 * 07/12/2018 - BW - Initial Creation
 * 02/15/2019 - BW - Continued development
 * 03/04/2019 - BW - Continued development
 * 03/05/2019 - BW - Continued development
 * 03/06/2019 - BW - Continued development
 * 03/11/2019 - BW - Added: window.onstorage - for IE11 localStorage sync issue
 * 07/26/2021 - BW - Modified: Updated for V6
 * 08/05/2021 - BW - Modified: added code for _storageAvailable
 *
 * Description/Purpose:
 * Session Time Out JS
 ************************************************************** */
import StackTraceError from "component/stack-trace-error/stack-trace-error";
import { COMMON, COMMON_TAG_DATA } from "component/session-time-out/session-time-out-util";
import PUBSUB_EVENT from "constant/pubsub-event";
import PubSub from 'pubsub-js';
import { TAG_DATA } from "constant/app-constant";
import LinkUtil from "util/link-util";
import CountDownTimer from "component/count-down-timer/count-down-timer";
import CommonUtil from "util/common-util";
import WindowModal from "component/modal/window-modal";
import Modal from "component/modal/modal";
import ModalUtil from "util/modal-util";

const SessionTimeOut = {


  /**
     * Init
     * @returns {SessionTimeOut}
     */
  init() {
    if (!this._storageAvailable()) return false;

    this._setUpVariable();
    this._cacheDom();

    // Only start monitoring if authorized
    if (this._isAuthorized) {
      if (this._isDebug) {
        CommonUtil.show($('#modal-content'));
        CommonUtil.show(this._$sessionTimeOutDebug1);
        CommonUtil.show(this._$sessionTimeOutDebug2);
        CommonUtil.show(this._$sessionTimeOutDebug3);
      }

      this._registerEvent();

      this._isSessionTimeOutInit = true;

      this._countDownTimer = new CountDownTimer(this._sessionWarningTime / 60 / 1000);
      // Continue/Start session recording
      this._continueSession();
      // Set Timeout (runs once every _interval)
      this._intervalId = setInterval(this._checkActivity.bind(this), this._interval);
    } else {
      this._resetLocalStorageLastActivity();
    }

    return this;
  },


  /**
     * Set up some common variables
     * @private
     */
  _setUpVariable() {
    try {
      this._sessionTimeOutObject = COMMON.SESSION_TIME_OUT_OBJECT;
      this._localStorageLastActivityVariable = COMMON.CASE_ANYWHERE_LAST_ACTIVITY;
      this._isDebug = false;
      this._isSessionTimeOutInit = false;
      this._intervalId = 0;
      this._intervalIdForWarningMessage = 0;
      this._browserTitle = document.title;
    } catch (e) {
      StackTraceError.processError(e, 'Session Time Out - Set Up Variable');
    }
  },


  /**
     * Caches some often used DOM objects
     * @returns {SessionTimeOut}
     * @private
     */
  _cacheDom() {
    try {
      this._document = $(document);
      this._isAuthorized = TAG_DATA.IS_AUTHORIZED;
      this._sessionTimeOutServlet = COMMON_TAG_DATA.SESSION_TIME_OUT_SERVLET_URL;
      this._logoutSessionLink = COMMON_TAG_DATA.LOGOUT_SESSION_LINK;
      this._interval = COMMON_TAG_DATA.SESSION_TIME_OUT_INTERVAL;
      this._intervalForWarningMessage = COMMON_TAG_DATA.SESSION_TIME_OUT_INTERVAL_FOR_WARNING_MESSAGE;
      // Removing 1 minute to account for FE/server discrepancies
      this._sessionTime = (parseInt(COMMON_TAG_DATA.SESSION_TIME_OUT_IN_MINS) - 1) * 60 * 1000;
      this._sessionWarningTime = parseInt(COMMON_TAG_DATA.SESSION_TIME_OUT_WARNING_TIME);
      this._$sessionWarningContainer = $(COMMON.SESSION_WARNING_CONTAINER_SELECTOR);
      this._$sessionTimeOutDebug1 = $(COMMON.SESSION_TIME_OUT_DEBUG_1_SELECTOR);
      this._$sessionTimeOutDebug2 = $(COMMON.SESSION_TIME_OUT_DEBUG_2_SELECTOR);
      this._$sessionTimeOutDebug3 = $(COMMON.SESSION_TIME_OUT_DEBUG_3_SELECTOR);
    } catch (e) {
      StackTraceError.processError(e, 'Session Time Out - Cache Dom');
    }
    return this;
  },


  /**
     * Register events or DOM event
     */
  _registerEvent() {
    // this._document
    //     .on('mousemove keydown DOMMouseScroll mousewheel mousedown touchstart touchmove',
    //         ($event) => this._recordLastActivity($event));
    this._document.bindWithDelay('mousemove keydown DOMMouseScroll mousewheel mousedown touchstart touchmove',
      {}, this._recordLastActivity.bind(this), 5000, true);
    PubSub.subscribe(PUBSUB_EVENT.COUNT_DOWN_TIMER_RUNNING, this._countDownTimerRunningEvent.bind(this));
    PubSub.subscribe(PUBSUB_EVENT.COUNT_DOWN_TIMER_DONE, this._countDownTimerDoneEvent.bind(this));
    return this;
  },


  /**
     * Is the process initialized?
     * @returns {boolean|*}
     */
  isInit() {
    return this._isSessionTimeOutInit;
  },


  /**
     * tests to see if localstorage can be used - if not we skip
     * @private
     */
  _storageAvailable() {
    let storage;
    try {
      storage = window.localStorage;
      const x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return e instanceof DOMException && (
      // everything except Firefox
        e.code === 22
                // Firefox
                || e.code === 1014
                // test name field too, because code might not be present
                // everything except Firefox
                || e.name === 'QuotaExceededError'
                // Firefox
                || e.name === 'NS_ERROR_DOM_QUOTA_REACHED')
                // acknowledge QuotaExceededError only if there's something already stored
                && storage && storage.length !== 0;
    }
  },


  /**
     * Will reset the data
     * @private
     */
  _resetLocalStorageLastActivity() {
    localStorage.removeItem(this._localStorageLastActivityVariable);
  },


  /**
     * Will continue the session
     * @private
     */
  _continueSession() {
    // Stops countdown Timer if necessary
    this._countDownTimer.stop();

    // Record last activity
    this._recordLastActivity();

    // Clear Timeout for Warning Message (faster polling)
    this._clearIntervalForWarningMessage();

    // Reset browser title window
    this._updateBrowserTitle();
  },


  /**
     * Will check user's activity
     * @private
     */
  _checkActivity() {
    // Get the date right now and find out the difference in minutes from the user's last activity
    const isCountDownTimerRunning = this._countDownTimer.isRunning();
    const now = new Date();
    const localStorageLastActivityStr = localStorage.getItem(this._localStorageLastActivityVariable);

    // If localStorageLastActivityStr == null then it means the user logged out.
    if (localStorageLastActivityStr == null) {
      this._endSession();
      return;
    }

    if (this._isDebug) console.log(`localStorageLastActivityStr:${localStorageLastActivityStr}`);
    const localStorageLastActivity = new Date(localStorage.getItem(this._localStorageLastActivityVariable));
    // console.log("localStorageLastActivity:" + localStorageLastActivity);
    const diffStorage = now - localStorageLastActivity;
    const proceed = diffStorage <= this._interval;
    if (this._isDebug) console.log(`diffStorage:${diffStorage}`);
    // Now using the diffStorage, see if we need to ping the server.
    // We need to ping the server if the diffStorage <= _interval
    if (this._isDebug) console.log(`isCountDownTimerRunning:${isCountDownTimerRunning}`);
    if (!isCountDownTimerRunning) {
      if (this._isDebug) {
        this._$sessionTimeOutDebug3.append(`\nTIME SINCE LAST ACTIVITY: ${diffStorage / 1000} secs. `
                    + `  --  SHOW WARNING IN APPROX: ${(((this._sessionTime - this._sessionWarningTime) - diffStorage)) / 1000} secs.`);
        if (this._$sessionTimeOutDebug3.length) this._$sessionTimeOutDebug3.scrollTop(this._$sessionTimeOutDebug3[0].scrollHeight - this._$sessionTimeOutDebug3.height());
      }
      if (proceed) {
        this._pingServer();
        return;
      }

      // Since we didn't ping the server, we know the user may be inactive, so we need to check to see if we need to show
      // the warning message.
      if (diffStorage >= (this._sessionTime - this._sessionWarningTime) && !ModalUtil.isOpen(this.windowModal)) {
        if (this._isDebug) console.log("showWarningMessage- S H O W");
        this._showWarningMessage();
        return;
      }
      if (this._isDebug) console.log("no ping");
    } else {
      if (this._isDebug) console.log("Count Down is running");
      if (proceed) {
        if (this._isDebug) console.log("Count Down is running --- closeWarningMessage");
        this._closeWarningMessage();
      }
    }
  },


  /**
     * This function will record the user's last activity date.
     */
  _recordLastActivity($event) {
    if (this._isDebug && $event !== undefined) {
      this._$sessionTimeOutDebug2.append(`\nUSER ACTIVITY RECORDED (${$event.type}): ${new Date()}`);
      if (this._$sessionTimeOutDebug2.length) this._$sessionTimeOutDebug2.scrollTop(this._$sessionTimeOutDebug2[0].scrollHeight - this._$sessionTimeOutDebug2.height());
    }
    // Don't record if timer is running - make the user hit a button on the popup, unless
    // the user has another tab open - they can extend
    if (!this._countDownTimer.isRunning()) {
      const date = new Date();
      localStorage.setItem(this._localStorageLastActivityVariable, date);
      if (this._isDebug) console.log(`_localStorageLastActivityVariable:${localStorage.getItem(this._localStorageLastActivityVariable)}`);
    } else if (this._isDebug) console.log("recordLastActivity -- DOING NOTHING");
  },


  /**
     * This function will record the user's last activity date.
     */
  recordLastActivityPublic(activity) {
    if (this && activity !== undefined && activity !== null) this._recordLastActivity(new Event(activity));
  },


  /**
     * Simply calls an Ajax to send a request to the server so the webserver will update the session timeout
     * @private
     */
  _pingServer() {
    const request = $.get({
      url: this._sessionTimeOutServlet,
      data: null,
      dataType: "text",
      cache: false,
      async: true, // Needs to be set to TRUE so we can also run other ajax as well ajax
    });
    request.done((data) => {
      if (this._isDebug) console.log("ping server success");
    });

    // $.ajax({
    //   success: function() {
    //     if (this._isDebug) console.log("ping server success");
    //   }
    // });
  },


  /**
     * Clears the Interval For the Warning Message (much faster polling mainly used for multiple tabs)
     * @private
     */
  _clearIntervalForWarningMessage() {
    // Stop the polling during the warning message (much faster polling when the message pops up)
    clearInterval(this._intervalIdForWarningMessage);
  },


  /**
     * We want to stop the polling of checkActivity and show the modal
     * @private
     */
  _showWarningMessage() {
    this._clearIntervalForWarningMessage();
    // Show the Warning Message
    this._openModal();
    // Start the Timer
    this._countDownTimer.start($(COMMON.SESSION_TIMER_SELECTOR));
    // Start polling the checkActivity at a faster pace
    this._intervalIdForWarningMessage = setInterval(this._checkActivity.bind(this), this._intervalForWarningMessage);
  },


  /**
     * We want to close the Warning message and reset everything
     */
  _closeWarningMessage() {
    if (this._isDebug) console.log("closeWarningMessage - C L O S E");
    // Close the Warning Message
    this.windowModal.close();
    // alert('close - todo');
    // TODO
    // Continue
    this._continueSession();
  },


  /**
     * ent that fires when the countdown is still running (every 1 second)
     * @param eventName
     * @param data
     * @private
     */
  _countDownTimerRunningEvent(eventName, data) {
    // returnArr will have 2 values
    // [0] - $elem
    // [1] - actual timer html value
    // Update the browser title
    this._updateBrowserTitle(` - ${data[1]}`);
  },


  /**
     * Event that fires when the countdown is finished
     * @param eventName
     * @param $elem
     * @private
     */
  _countDownTimerDoneEvent(eventName, $elem) {
    // End the session since the countdown finished and there was no response from the user.
    this._endSession();
  },


  /**
     * Ends the session
     * @private
     */
  _endSession() {
    this._countDownTimer.stop();
    // Reset the _localStorageLastActivityVariable
    this._resetLocalStorageLastActivity();
    LinkUtil.openBasic(null, null, this._logoutSessionLink);
    // eval("top.document.location.href='" + $("#logoutSessionLinkId").val() + "'");
  },


  /**
     * Will update the browser title window
     * @param additionalText
     * @private
     */
  _updateBrowserTitle(additionalText) {
    document.title = this._browserTitle
            + (additionalText !== undefined && additionalText !== null ? additionalText : "");
  },


  /**
     * Opens the Modal Window
     * @private
     */
  _openModal() {
    this.windowModal = new WindowModal({
      footer: true,
      closeMethods: ['overlay'],
      cssClass: ['session-time-out-modal', 'modal-max-width'],
    })
      .addHeader('Session Time Out Warning', false)
      .addCustomIcon()
      .setContent(this._$sessionWarningContainer.prop('outerHTML'));

    // When $addEFileUrlButton = true then we want 2 buttons, if false remove the 'ok' button and
    // update the Modal.[classes] appropriately
    // let returnButtonCssClass = Modal.secondaryFooterButtonFloatRightCssClass;
    // if ($addEFileUrlButton) {
    // Only show the 'ok' when the $addEFileUrlButton
    this.windowModal
      .addFooterButton('Continue', Modal.primaryFooterButtonWithOkCssClass,
        () => {
          this._continueSession();
          this.windowModal.close();
        });
    // } else {
    //     Only one button, so don't float-right and change to primary button
    // returnButtonCssClass = Modal.primaryFooterButtonCssClass;
    // }

    // Always showing the 'Logout' button
    this.windowModal
      .addFooterButton('Logout',
        `${Modal.secondaryFooterButtonCssClass} logout-button`,
        () => {
          this._endSession();
          this.windowModal.close();
        });
    this.windowModal.open();
  },


};

/**
 * Workaround for IE11 localStorage sync issue
 */
window.onstorage = function (e) {
  // Leave blank
};

export default SessionTimeOut;
